import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { getApiBaseUrl } from '@/lib/api';
import { useThemeQuery, useThemeQueryClient } from '@/hooks/useThemeQuery';
import { QUERY_KEYS } from '@/lib/queryKeys';
import { resolveUploadUrl } from '@/lib/assets/resolveUploadUrl';
import { getPublicUiBootstrap } from '@/lib/branding/publicUiBootstrap';
import type { PublicLoadingConfig } from '@/lib/branding/types';
import { syncLoadingBlockCaches } from '@/lib/branding/syncLoadingBlockCaches';
// Types
export interface ThemeSettings {
  primary_color: string;
  accent_color: string;
  success_color: string;
  warning_color: string;
  sidebar_bg_color: string;
  is_dark_mode: boolean;
}

export interface BrandingSettings {
  site_title: string;
  logo_url: string | null;
  favicon_url: string | null;
  loading_image_url: string | null;
}

export interface UISettings {
  enable_animations: boolean;
  enable_glass_effect: boolean;
  loading_animation_type: 'default' | 'minimal' | 'pulse' | 'dots';
  /** Boot loader indicator animation duration (ms); applied to CSS/motion in PageLoader. Default 1000. */
  loader_indicator_duration_ms?: number;
}

export interface AllSettings {
  theme: ThemeSettings;
  branding: BrandingSettings;
  ui: UISettings;
}

interface SiteSettingsContextType {
  settings: AllSettings;
  isLoading: boolean;
  updateTheme: (theme: Partial<ThemeSettings>) => Promise<void>;
  updateBranding: (branding: Partial<BrandingSettings>) => Promise<void>;
  updateUI: (ui: Partial<UISettings>) => Promise<void>;
  refreshSettings: () => Promise<void>;
}

// Default values - không dùng hardcoded fallback cho branding
// Fresh visitor (no cache) defaults to light to avoid black first paint; cached/API can set dark.
const defaultSettings: AllSettings = {
  theme: {
    primary_color: '207 90% 42%',
    accent_color: '187 85% 43%',
    success_color: '160 84% 39%',
    warning_color: '38 92% 50%',
    sidebar_bg_color: '210 50% 15%',
    is_dark_mode: false
  },
  branding: {
    site_title: '',  // Để trống nếu không có
    logo_url: null,
    favicon_url: null,
    loading_image_url: null
  },
  ui: {
    enable_animations: true,
    enable_glass_effect: true,
    loading_animation_type: 'default',
    loader_indicator_duration_ms: 1000
  }
};

const SETTINGS_CACHE_KEY = 'site_settings_cache';

const mergePublicUiIntoSettings = (base: AllSettings, publicUi: PublicLoadingConfig | null): AllSettings => {
  if (!publicUi) return base;
  const ui: UISettings = {
    ...base.ui,
    loading_animation_type: publicUi.loading_animation_type,
    loader_indicator_duration_ms: publicUi.loader_indicator_duration_ms,
    enable_animations: publicUi.enable_animations,
  };
  return { ...base, ui };
};

const resolveInitialSettings = (): AllSettings => {
  const cached = loadCachedSettings();
  const bootstrapBranding = (window as Window & { __PUBLIC_BRANDING__?: BrandingSettings }).__PUBLIC_BRANDING__;
  const publicUi = getPublicUiBootstrap();

  let base: AllSettings;
  if (bootstrapBranding) {
    base = { ...(cached || defaultSettings), branding: bootstrapBranding };
  } else {
    base = cached || defaultSettings;
  }

  // Public API loading config overrides defaultSettings.ui for fresh visitors
  return mergePublicUiIntoSettings(base, publicUi);
};

// Load cached settings from localStorage (if any)
const loadCachedSettings = (): AllSettings | null => {
  try {
    const cached = localStorage.getItem(SETTINGS_CACHE_KEY);
    if (cached) {
      return JSON.parse(cached);
    }
  } catch {
    // Ignore parse errors
  }
  return null;
};

// Save settings to localStorage cache
const saveCachedSettings = (settings: AllSettings) => {
  try {
    localStorage.setItem(SETTINGS_CACHE_KEY, JSON.stringify(settings));
  } catch {
    // Ignore storage errors
  }
};

const SiteSettingsContext = createContext<SiteSettingsContextType | undefined>(undefined);

const API_URL = getApiBaseUrl();

export const SiteSettingsProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  // FIX: Initialize from bootstrap branding (window.__PUBLIC_BRANDING__) hoặc cache
  const [settings, setSettings] = useState<AllSettings>(() => resolveInitialSettings());
  const [isLoading, setIsLoading] = useState(true);
  
  // Use React Query to fetch theme (with 5-10 min cache)
  const themeQuery = useThemeQuery();
  const queryClient = useThemeQueryClient();

  // Apply theme to CSS variables
  const applyTheme = (theme: ThemeSettings) => {
    const root = document.documentElement;
    
    // Apply primary color
    root.style.setProperty('--primary', theme.primary_color);
    root.style.setProperty('--ring', theme.primary_color);

    // Apply accent color
    root.style.setProperty('--accent', theme.accent_color);
    
    // Apply other colors
    root.style.setProperty('--success', theme.success_color);
    root.style.setProperty('--warning', theme.warning_color);
    root.style.setProperty('--sidebar-background', theme.sidebar_bg_color);
    
  };

  // REFACTORED: Không tự apply DOM nữa - PublicBrandingContext/BrandingDomApplier là single source of truth
  // Chỉ sync branding state với PublicBrandingContext nếu có
  // applyBranding() đã bị xóa - không mutate DOM ở đây nữa

  // Apply UI settings
  const applyUI = (ui: UISettings) => {
    const root = document.documentElement;
    
    if (!ui.enable_animations) {
      root.style.setProperty('--animation-duration', '0ms');
      root.classList.add('no-animations');
    } else {
      root.style.removeProperty('--animation-duration');
      root.classList.remove('no-animations');
    }
    
    if (!ui.enable_glass_effect) {
      root.classList.add('no-glass');
    } else {
      root.classList.remove('no-glass');
    }
  };

  // Get theme from React Query cache or fallback to default
  const getTheme = (): ThemeSettings => {
    if (themeQuery.data) {
      return themeQuery.data;
    }
    // Fallback to cached or default if query hasn't loaded yet
    const cached = loadCachedSettings();
    return cached?.theme || defaultSettings.theme;
  };

  // Fetch all settings from API (uses cache as fallback for 429/network errors)
  const fetchSettings = async () => {
    const publicUi = getPublicUiBootstrap();
    // Apply cached settings immediately (so UI is never blank)
    const cached = loadCachedSettings();
    const cachedWithPublicUi = cached ? mergePublicUiIntoSettings(cached, publicUi) : null;
    if (cachedWithPublicUi) {
      applyTheme(cachedWithPublicUi.theme);
      // REFACTORED: Không apply branding DOM ở đây nữa - PublicBrandingContext/BrandingDomApplier xử lý
      applyUI(cachedWithPublicUi.ui);
    } else if (publicUi) {
      applyUI(mergePublicUiIntoSettings(defaultSettings, publicUi).ui);
    }

    // Get theme from React Query (cached or fresh)
    // React Query handles caching automatically (5-10 min)
    const systemTheme = getTheme();
    applyTheme(systemTheme);

    // FIX: Không fetch public branding ở đây nữa vì đã bootstrap trong index.html
    // Chỉ update cache nếu có bootstrap branding từ window.__PUBLIC_BRANDING__
    const bootstrapBranding = (window as any).__PUBLIC_BRANDING__;
    if (bootstrapBranding) {
      // Update cache với bootstrap branding
      const updatedCache = {
        ...(cached || defaultSettings),
        branding: bootstrapBranding,
        theme: systemTheme,
      };
      saveCachedSettings(updatedCache);
      
      // Update state nếu chưa có branding hợp lệ
      if (!settings.branding.site_title && bootstrapBranding.site_title) {
        setSettings(prev => ({
          ...prev,
          branding: bootstrapBranding,
        }));
      }
      
      if (import.meta.env.DEV) {
        console.debug('[branding] context fetch status: Using bootstrap branding from index.html');
      }
    }

    // Try to fetch other settings (branding, UI) - requires auth
    // IMPORTANT: Only fetch if token exists to avoid 401 errors
    const token = localStorage.getItem('token');
    if (!token) {
      // No token - use cached or default settings (public branding already applied above)
      if (import.meta.env.DEV) {
        console.log('[SiteSettingsContext] No token, skipping /settings/all fetch, using cached/default');
      }
      const fallback = mergePublicUiIntoSettings(cached || defaultSettings, publicUi);
      const updatedFallback = {
        ...fallback,
        theme: systemTheme,
      };
      setSettings(updatedFallback);
      applyTheme(updatedFallback.theme);
      // REFACTORED: Không apply branding DOM ở đây nữa - PublicBrandingContext/BrandingDomApplier xử lý
      applyUI(updatedFallback.ui);
      saveCachedSettings(updatedFallback);
      setIsLoading(false);
      return;
    }

    try {
      const headers: Record<string, string> = {
        Authorization: `Bearer ${token}`,
      };

      const response = await fetch(`${API_URL}/settings/all`, {
        headers,
      });

      // Handle rate limit or other errors gracefully
      if (response.ok !== true) {
        // 401 means no auth - use cached/default (shouldn't happen if token check above works)
        if (response.status === 401) {
          if (import.meta.env.DEV) {
            console.warn('[SiteSettingsContext] 401 Unauthorized - token may be invalid, using cached/default');
          }
        } else {
          if (import.meta.env.DEV) {
            console.warn(`[SiteSettingsContext] Settings API returned ${response.status}, using cached/default`);
          }
        }
        // Still use what we have (cached or default)
        const fallback = cached || defaultSettings;
        // Update theme from system (already fetched) - system-wide
        const updatedFallback = {
          ...fallback,
          theme: systemTheme,
        };
        setSettings(updatedFallback);
        applyTheme(updatedFallback.theme);
        // REFACTORED: Không apply branding DOM ở đây nữa
        applyUI(updatedFallback.ui);
        saveCachedSettings(updatedFallback);
        return;
      }

      const data = await response.json();
      if (data.success && data.data) {
        // Always use system theme (from public endpoint), not from /all
        const newSettings: AllSettings = {
          theme: systemTheme, // System-wide theme from public endpoint
          branding: data.data.branding || defaultSettings.branding,
          ui: data.data.ui || defaultSettings.ui
        };
        setSettings(newSettings);
        saveCachedSettings(newSettings); // Cache for next time
        applyTheme(newSettings.theme);
        // REFACTORED: Không apply branding DOM ở đây nữa - PublicBrandingContext/BrandingDomApplier xử lý
        applyUI(newSettings.ui);
      }
    } catch (error) {
      console.error('Failed to fetch settings:', error);
      // Use cached or default settings on network error, but use system theme
      const fallback = cached || defaultSettings;
      const updatedFallback = {
        ...fallback,
        theme: systemTheme, // System-wide theme
      };
      setSettings(updatedFallback);
      applyTheme(updatedFallback.theme);
      // REFACTORED: Không apply branding DOM ở đây nữa - PublicBrandingContext/BrandingDomApplier xử lý
      applyUI(updatedFallback.ui);
      saveCachedSettings(updatedFallback);
    } finally {
      setIsLoading(false);
    }
  };

  // Update theme settings (system-wide, admin only)
  const updateTheme = async (theme: Partial<ThemeSettings>) => {
    try {
      const token = localStorage.getItem('token');
      const headers: Record<string, string> = {
        'Content-Type': 'application/json',
      };
      if (token) headers.Authorization = `Bearer ${token}`;

      const response = await fetch(`${API_URL}/settings/theme`, {
        method: 'PUT',
        headers,
        body: JSON.stringify(theme),
      });

      if (response.ok !== true) {
        const text = await response.text().catch(() => '');
        throw new Error(`HTTP ${response.status} ${response.statusText} ${text}`.trim());
      }

      // Some backends return 200 with { success: false } instead of 403
      const payload = await response.json().catch(() => null as any);
      if (payload && payload.success === false) {
        throw new Error(payload.message || 'Không thể lưu cài đặt');
      }

      // Get updated theme from response (system-wide)
      const updatedTheme = payload?.data || { ...settings.theme, ...theme };
      
      // Update React Query cache immediately (optimistic update)
      // This makes UI update instantly without waiting for refetch
      // Optimistically update cache after theme update
      queryClient.setQueryData<ThemeSettings>(QUERY_KEYS.settings.theme, updatedTheme);
      
      // Also update local state and cache
      const newSettings = { ...settings, theme: updatedTheme };
      setSettings(newSettings);
      saveCachedSettings(newSettings); // Update localStorage cache
      applyTheme(updatedTheme);
      
      // Optionally invalidate query to refetch fresh data in background
      // This ensures we have the latest from server, but UI already updated
      // Invalidate theme cache to refetch updated theme
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.settings.theme });
    } catch (error) {
      console.error('Failed to update theme:', error);
      throw error;
    }
  };

  // Update branding settings
  const updateBranding = async (branding: Partial<BrandingSettings>) => {
    try {
      const token = localStorage.getItem('token');
      const headers: Record<string, string> = {
        'Content-Type': 'application/json',
      };
      if (token) headers.Authorization = `Bearer ${token}`;

      const response = await fetch(`${API_URL}/settings/branding`, {
        method: 'PUT',
        headers,
        body: JSON.stringify(branding),
      });

      if (response.ok !== true) {
        const text = await response.text().catch(() => '');
        throw new Error(`HTTP ${response.status} ${response.statusText} ${text}`.trim());
      }

      const payload = await response.json().catch(() => null as any);
      if (payload && payload.success === false) {
        throw new Error(payload.message || 'Không thể lưu cài đặt');
      }

      const updatedBranding = payload?.data || { ...settings.branding, ...branding };
      const newBranding = { ...settings.branding, ...updatedBranding };
      const newSettings = { ...settings, branding: newBranding };
      setSettings(newSettings);
      saveCachedSettings(newSettings);
      syncLoadingBlockCaches(newSettings);
    } catch (error) {
      console.error('Failed to update branding:', error);
      throw error;
    }
  };

  // Update UI settings
  const updateUI = async (ui: Partial<UISettings>) => {
    try {
      const token = localStorage.getItem('token');
      const headers: Record<string, string> = {
        'Content-Type': 'application/json',
      };
      if (token) headers.Authorization = `Bearer ${token}`;

      const response = await fetch(`${API_URL}/settings/ui`, {
        method: 'PUT',
        headers,
        body: JSON.stringify(ui),
      });

      if (response.ok !== true) {
        const text = await response.text().catch(() => '');
        throw new Error(`HTTP ${response.status} ${response.statusText} ${text}`.trim());
      }

      const payload = await response.json().catch(() => null as any);
      if (payload && payload.success === false) {
        throw new Error(payload.message || 'Không thể lưu cài đặt');
      }

      const updatedUI = payload?.data || { ...settings.ui, ...ui };
      const newSettings = { ...settings, ui: updatedUI };
      setSettings(newSettings);
      saveCachedSettings(newSettings);
      applyUI(updatedUI);
      syncLoadingBlockCaches(newSettings);
    } catch (error) {
      console.error('Failed to update UI settings:', error);
      throw error;
    }
  };

  // Initial fetch - apply theme and branding immediately to avoid FOUC
  useEffect(() => {
    // FIX: Hydrate từ bootstrap branding (window.__PUBLIC_BRANDING__) nếu có
    const bootstrapBranding = (window as any).__PUBLIC_BRANDING__;
    const cached = loadCachedSettings();
    
    if (cached) {
      applyTheme(cached.theme);
      // FIX: Nếu có bootstrap branding thì dùng nó (không dùng cached branding cũ)
      if (bootstrapBranding) {
        // REFACTORED: Không apply branding DOM ở đây nữa - PublicBrandingContext/BrandingDomApplier xử lý
      } else {
        // REFACTORED: Không apply branding DOM ở đây nữa
      }
    } else {
      // Apply default settings if no cache
      applyTheme(defaultSettings.theme);
      // REFACTORED: Không apply branding DOM ở đây nữa - PublicBrandingContext/BrandingDomApplier xử lý
    }
    
    fetchSettings();
  }, []);

  // React Query automatically refetches on window focus (refetchOnWindowFocus: true)
  // Update local state when theme query data changes
  useEffect(() => {
    if (themeQuery.data) {
      const systemTheme = themeQuery.data;
      if (JSON.stringify(systemTheme) !== JSON.stringify(settings.theme)) {
        // Theme changed, update state and cache
        const newSettings = { ...settings, theme: systemTheme };
        setSettings(newSettings);
        saveCachedSettings(newSettings);
        applyTheme(systemTheme);
      }
    }
  }, [themeQuery.data, settings.theme]);

  // STEP 1 (loader consolidation): Do not block children when isLoading.
  // Theme/cache are applied in initial useEffect and inside fetchSettings() to avoid FOUC.
  return (
    <SiteSettingsContext.Provider value={{
      settings,
      isLoading,
      updateTheme,
      updateBranding,
      updateUI,
      refreshSettings: fetchSettings
    }}>
      {children}
    </SiteSettingsContext.Provider>
  );
};

export const useSiteSettings = () => {
  const context = useContext(SiteSettingsContext);
  if (context === undefined) {
    throw new Error('useSiteSettings must be used within a SiteSettingsProvider');
  }
  return context;
};

export const useOptionalSiteSettings = () => {
  return useContext(SiteSettingsContext) ?? null;
};
